import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { apiFetchBlob } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useOverlays } from './overlay-context';
import {
  deleteAttachment,
  isImageAttachment,
  listWorkItemAttachments,
  previewUrlForAttachment,
  uploadWorkItemDocument,
  type AttachmentDto,
} from '../../lib/attachments';

function AttachmentImage({ row }: { row: AttachmentDto }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void previewUrlForAttachment(row.download_url)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [row.download_url]);

  if (!src) return null;
  return (
    <img
      src={src}
      alt={row.filename}
      className="mx-auto block max-h-72 w-auto max-w-full rounded-md border object-contain bg-muted/40"
    />
  );
}

export default function AttachmentList({
  projectId,
  workItemId,
  readOnly,
  requiredDocuments = [],
  documentGuidance = {},
  onChanged,
  title = 'Attachments',
}: {
  projectId: string;
  workItemId: string;
  readOnly: boolean;
  requiredDocuments?: string[];
  documentGuidance?: Record<string, string>;
  onChanged?: () => void;
  title?: string;
}) {
  const { toast, confirm } = useOverlays();
  const [items, setItems] = useState<AttachmentDto[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const requirementRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await listWorkItemAttachments(projectId, workItemId));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to load attachments');
    }
  }, [projectId, workItemId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onUpload(file: File, requirementLabel: string | null) {
    setBusy(true);
    try {
      await uploadWorkItemDocument(projectId, workItemId, file, requirementLabel);
      await load();
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  function chooseFile(requirementLabel: string | null) {
    requirementRef.current = requirementLabel;
    inputRef.current?.click();
  }

  async function onOpen(row: AttachmentDto, event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    try {
      const blob = await apiFetchBlob(row.download_url);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = row.filename;
      link.rel = 'noopener';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Download failed');
    }
  }

  async function onDelete(row: AttachmentDto) {
    const ok = await confirm({
      title: 'Delete attachment',
      description: `Delete ${row.filename}? Embedded images that use this file will break.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteAttachment(row.id);
      await load();
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">{title}</h2>
        {readOnly ? null : (
          <>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                const requirementLabel = requirementRef.current;
                requirementRef.current = null;
                event.target.value = '';
                if (file) void onUpload(file, requirementLabel);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => chooseFile(null)}
            >
              {busy ? 'Uploading...' : 'Upload'}
            </Button>
          </>
        )}
      </div>
      {requiredDocuments.length > 0 ? (
        <div className="space-y-2 rounded-md border p-3">
          <div>
            <p className="text-sm font-medium">Required documents</p>
            <p className="text-xs text-muted-foreground">Upload each document before submitting this request.</p>
          </div>
          <ul className="space-y-2">
            {requiredDocuments.map((document) => {
              const supplied = items.some((item) => item.requirement_label?.toLocaleLowerCase() === document.toLocaleLowerCase());
              return (
                <li key={document} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{document}</span>
                    {documentGuidance[document] ? (
                      <span className="mt-0.5 block text-xs text-muted-foreground">{documentGuidance[document]}</span>
                    ) : null}
                  </span>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={supplied ? 'secondary' : 'outline'}>{supplied ? 'Uploaded' : 'Missing'}</Badge>
                    {readOnly ? null : (
                      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => chooseFile(document)}>
                        {supplied ? 'Upload another' : 'Upload'}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files attached.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => (
            <li key={row.id} className="space-y-2">
              {isImageAttachment(row) ? <AttachmentImage row={row} /> : null}
              <div className="flex items-center justify-between gap-2 text-sm">
                <a
                  href={row.download_url}
                  className="truncate underline-offset-2 hover:underline"
                  onClick={(event) => void onOpen(row, event)}
                >
                  {row.filename}
                </a>
                {row.requirement_label ? <Badge variant="outline">{row.requirement_label}</Badge> : null}
                {readOnly ? null : (
                  <Button type="button" size="sm" variant="ghost" onClick={() => void onDelete(row)}>
                    Delete
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
